import React from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Code2,
  FileCheck2,
  HandHeart,
  Landmark,
  LockKeyhole,
  Rocket,
  ShieldCheck,
  Users,
  Wallet,
} from "lucide-react";

const FUNDING_GOAL = 10000;
const CURRENTLY_RAISED = 0; // Update this as contributions come in.
const progress = Math.min(
  Math.round((CURRENTLY_RAISED / FUNDING_GOAL) * 100),
  100
);

const contributionLevels = [
  {
    amount: 25,
    title: "Friend",
    description: "Help keep the MastaKash journey moving.",
  },
  {
    amount: 50,
    title: "Builder",
    description: "Support technology and operational continuity.",
  },
  {
    amount: 100,
    title: "Champion",
    description: "Help fund the next stage of readiness.",
  },
  {
    amount: 250,
    title: "Partner",
    description: "Help us move from survival toward readiness.",
  },
  {
    amount: 500,
    title: "Strategic Supporter",
    description: "Make a meaningful contribution to our bridge.",
  },
  {
    amount: 1000,
    title: "Anchor",
    description: "Help finance a significant portion of the immediate bridge.",
  },
  {
    amount: 2500,
    title: "Founding Supporter",
    description: "Help us cross a major part of the bridge.",
  },
  {
    amount: 5000,
    title: "Lead Supporter",
    description: "Help finance half of the immediate campaign.",
  },
];

const useOfFunds = [
  {
    icon: Landmark,
    title: "Technology & Infrastructure",
    percentage: "20%",
    amount: "$2,000",
    description:
      "Hosting, infrastructure, backups and the systems required to keep MastaKash operational.",
  },
  {
    icon: ShieldCheck,
    title: "Product & Security",
    percentage: "15%",
    amount: "$1,500",
    description:
      "Product maintenance, security improvements, technical operations and critical fixes.",
  },
  {
    icon: FileCheck2,
    title: "Regulatory Readiness",
    percentage: "20%",
    amount: "$2,000",
    description:
      "Regulatory, compliance and readiness activities supporting our authorization journey.",
  },
  {
    icon: LockKeyhole,
    title: "Professional Services",
    percentage: "10%",
    amount: "$1,000",
    description:
      "Professional support, documentation, governance and related business requirements.",
  },
  {
    icon: Rocket,
    title: "Pilot & Business Readiness",
    percentage: "10%",
    amount: "$1,000",
    description:
      "Preparation for stakeholder engagement, pilot activities and future commercial deployment.",
  },
  {
    icon: Users,
    title: "Investor Engagement",
    percentage: "10%",
    amount: "$1,000",
    description:
      "Investor communications, fundraising materials and strategic capital engagement.",
  },
  {
    icon: Wallet,
    title: "Operational Reserve",
    percentage: "15%",
    amount: "$1,500",
    description:
      "A controlled reserve for essential unforeseen operational requirements.",
  },
];

const bridgeSteps = [
  {
    number: "01",
    title: "Survive",
    description:
      "Keep the technology, infrastructure and core operations running.",
  },
  {
    number: "02",
    title: "Strengthen",
    description:
      "Continue product, security, compliance and regulatory-readiness work.",
  },
  {
    number: "03",
    title: "Prepare",
    description:
      "Build pilot, business and investor readiness for the next stage.",
  },
  {
    number: "04",
    title: "Scale",
    description:
      "Pursue strategic capital and move toward commercial deployment.",
  },
];

const faqs = [
  {
    question: "What is MastaKash?",
    answer:
      "MastaKash is a Kenyan-built financial technology platform designed to help chamas, savings groups and community-based financial organizations manage their financial activities digitally.",
  },
  {
    question: "Is MastaKash already built?",
    answer:
      "Yes. MastaKash has progressed substantially beyond the idea stage. Significant product and technology development has already been completed.",
  },
  {
    question: "Why are you raising $10,000?",
    answer:
      "The $10,000 is an immediate survival and regulatory-readiness bridge fund. It is intended to maintain operations, support critical readiness activities and create runway while pursuing the larger strategic capital raise.",
  },
  {
    question: "Does the $10,000 satisfy the CBK capital requirement?",
    answer:
      "No. The $10,000 campaign is not being presented as the full capitalization requirement for MastaKash. It is bridge funding intended to support operations, readiness and the pursuit of the larger capital raise.",
  },
  {
    question: "Does contributing guarantee a MastaKash licence?",
    answer:
      "No. Contributions support MastaKash's continued operations and regulatory readiness. Any regulatory authorization remains subject to the applicable legal and regulatory requirements and the regulator's assessment.",
  },
  {
    question: "How will the money be used?",
    answer:
      "Funds will primarily support technology infrastructure, product maintenance, security, regulatory readiness, professional services, pilot preparation, investor engagement and essential operating requirements.",
  },
  {
    question: "Can I support MastaKash without giving money?",
    answer:
      "Absolutely. Introductions to investors, strategic partners, fintech professionals, institutions and potential customers can be extremely valuable. Sharing the campaign also helps.",
  },
];

function SectionHeading({
  eyebrow,
  title,
  description,
  centered = false,
}) {
  return (
    <div className={centered ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-orange-500">
        {eyebrow}
      </p>

      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
        {title}
      </h1>

      {description && (
        <p className="mt-5 text-lg leading-8 text-slate-600">
          {description}
        </p>
      )}
    </div>
  );
}

function MastaKashLogo() {
  return (
    <a
      href="#top"
      className="flex items-center gap-3"
      aria-label="MastaKash home"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500 text-xl font-black text-white shadow-lg shadow-orange-500/20">
        M
      </div>

      <div>
        <div className="text-xl font-extrabold tracking-tight text-white">
          Masta<span className="text-orange-400">Kash</span>
        </div>

        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-purple-200">
          Powered by RoshTech
        </div>
      </div>
    </a>
  );
}

function PrimaryButton({ children, href = "#support" }) {
  return (
    <a
      href={href}
      className="group inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-bold text-white shadow-xl shadow-orange-900/20 transition hover:-translate-y-0.5 hover:bg-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-300"
    >
      {children}
      <ArrowRight
        size={18}
        className="transition-transform group-hover:translate-x-1"
      />
    </a>
  );
}

function SecondaryButton({ children, href = "#story" }) {
  return (
    <a
      href={href}
      className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-bold text-white backdrop-blur transition hover:bg-white/15"
    >
      {children}
    </a>
  );
}

export default function MastaKashFundraising() {
  return (
    <div id="fundraising">

   
    <div
      id="top"
      className="min-h-screen overflow-x-hidden bg-white text-slate-900"
    >
      {/* ============================================================
          NAVIGATION
      ============================================================ */}
      <header className="absolute left-0 right-0 top-0 z-50">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8 lg:px-10">
          <MastaKashLogo />

          <nav className="hidden items-center gap-8 lg:flex">
            <a
              href="#story"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              Our Story
            </a>

            <a
              href="#fund"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              Why We Need It
            </a>

            <a
              href="#use-of-funds"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              Use of Funds
            </a>

            <a
              href="#faq"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              FAQ
            </a>
          </nav>

          <a
            href="#support"
            className="hidden rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-orange-400 sm:inline-flex"
          >
            Support MastaKash
          </a>
        </div>
      </header>

      {/* ============================================================
          HERO
      ============================================================ */}
      <section className="relative isolate overflow-hidden bg-[#29104d]">
        {/* Decorative background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute -left-40 top-10 h-[500px] w-[500px] rounded-full bg-purple-700/30 blur-3xl" />
          <div className="absolute -right-40 bottom-0 h-[600px] w-[600px] rounded-full bg-orange-500/10 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
        </div>

        <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.1fr_.9fr] lg:items-center lg:px-10 lg:pb-28 lg:pt-40">
          <div>
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-4 py-2 text-sm font-semibold text-orange-300">
              <span className="h-2 w-2 animate-pulse rounded-full bg-orange-400" />
              MastaKash Survival & Regulatory Readiness Fund
            </div>

            <h1 className="max-w-4xl text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
              We are not starting over.
              <span className="mt-2 block text-orange-400">
                We are trying to finish what we started.
              </span>
            </h1>

            <p className="mt-7 max-w-2xl text-lg leading-8 text-purple-100 sm:text-xl">
              Help us raise <strong className="text-white">$10,000</strong> to
              keep MastaKash operational, strengthen regulatory readiness and
              create the runway for our next stage of growth.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <PrimaryButton>Support MastaKash</PrimaryButton>
              <SecondaryButton href="#story">
                Discover Our Journey
              </SecondaryButton>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-purple-200">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-orange-400" />
                Kenyan-built fintech
              </div>

              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-orange-400" />
                Product already built
              </div>

              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-orange-400" />
                Regulatory journey underway
              </div>
            </div>
          </div>

          {/* Fund card */}
          <div
            id="fund"
            className="relative mx-auto w-full max-w-md lg:ml-auto"
          >
            <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-br from-orange-400/50 to-purple-400/10 blur-xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white p-7 shadow-2xl sm:p-9">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-500">
                    Immediate campaign target
                  </p>

                  <div className="mt-1 text-5xl font-black tracking-tight text-[#29104d] sm:text-6xl">
                    $10,000
                  </div>
                </div>

                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                  <CircleDollarSign size={26} />
                </div>
              </div>

              <div className="mt-8">
                <div className="mb-2 flex items-center justify-between text-sm font-semibold">
                  <span className="text-slate-600">Campaign progress</span>
                  <span className="text-[#29104d]">{progress}%</span>
                </div>

                <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-orange-500 transition-all duration-700"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="font-bold text-[#29104d]">
                    ${CURRENTLY_RAISED.toLocaleString()} raised
                  </span>

                  <span className="text-slate-500">
                    ${(FUNDING_GOAL - CURRENTLY_RAISED).toLocaleString()} to go
                  </span>
                </div>
              </div>

              <div className="my-7 h-px bg-slate-100" />

              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="mt-0.5 text-orange-500">
                    <Clock3 size={20} />
                  </div>

                  <div>
                    <p className="font-bold text-slate-900">
                      This is a bridge
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      Immediate funding to sustain operations and readiness
                      while we pursue larger strategic capital.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-0.5 text-orange-500">
                    <ShieldCheck size={20} />
                  </div>

                  <div>
                    <p className="font-bold text-slate-900">
                      Built for transparency
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      Funds are allocated against clearly defined campaign
                      priorities.
                    </p>
                  </div>
                </div>
              </div>

              <a
                href="#support"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-[#29104d] px-5 py-4 font-bold text-white transition hover:bg-[#3a1768]"
              >
                Help Us Cross the Bridge
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TRUST STRIP
      ============================================================ */}
      <section className="border-b border-slate-100 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-slate-100 px-5 sm:grid-cols-4 sm:px-8 lg:px-10">
          {[
            ["Years", "of product development"],
            ["$10K", "immediate bridge target"],
            ["Kenya", "built for Africa"],
            ["1 Goal", "reach the next milestone"],
          ].map(([value, label]) => (
            <div
              key={label}
              className="px-4 py-7 text-center first:pl-0 last:pr-0 sm:px-6"
            >
              <div className="text-2xl font-black text-[#29104d] sm:text-3xl">
                {value}
              </div>
              <div className="mt-1 text-xs font-medium text-slate-500 sm:text-sm">
                {label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================
          STORY
      ============================================================ */}
      <section id="story" className="scroll-mt-20 bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:items-center lg:px-10">
          <div>
            <SectionHeading
              eyebrow="The MastaKash Story"
              title="Africa's communities deserve better financial tools."
              description="MastaKash was born from a simple conviction: technology can help communities organize, manage and grow their collective financial activities more efficiently."
            />
          </div>

          <div className="space-y-6 text-base leading-8 text-slate-600">
            <p>
              Across Kenya and Africa, millions of people participate in
              chamas, savings groups, merry-go-rounds, table-banking groups
              and other community-based financial arrangements.
            </p>

            <p>
              These communities save together, lend to one another, make
              contributions, keep records and support each other financially.
              Yet many still depend on paper records, spreadsheets, messaging
              applications and fragmented manual processes.
            </p>

            <p>
              MastaKash was created to help change that by bringing community
              financial activities into a more structured digital environment.
            </p>

            <div className="rounded-2xl border-l-4 border-orange-500 bg-orange-50 p-6">
              <p className="text-lg font-bold leading-8 text-[#29104d]">
                "We are not asking people to fund an idea that exists only on
                paper. We are asking them to help protect years of work and
                give that work the runway to reach its next chapter."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          WHY NOW
      ============================================================ */}
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <SectionHeading
            eyebrow="Why We Are Raising"
            title="The product is built. The journey is underway. We need runway."
            description="MastaKash has reached a stage where maintaining momentum requires immediate financial support."
            centered
          />

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Code2,
                title: "Keep the technology alive",
                text: "Hosting, infrastructure, maintenance, security and technical operations cannot simply stop while the larger funding process continues.",
              },
              {
                icon: FileCheck2,
                title: "Continue regulatory readiness",
                text: "The authorization journey requires continued preparation, documentation, governance and professional support.",
              },
              {
                icon: Rocket,
                title: "Prepare for the next stage",
                text: "We need the capacity to engage strategic investors, partners and stakeholders while preparing the platform for pilot and commercial deployment.",
              },
            ].map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100 text-[#5b21b6] transition group-hover:bg-orange-100 group-hover:text-orange-600">
                    <Icon size={24} />
                  </div>

                  <h3 className="mt-6 text-xl font-extrabold text-slate-900">
                    {item.title}
                  </h3>

                  <p className="mt-3 leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          USE OF FUNDS
      ============================================================ */}
      <section id="use-of-funds" className="scroll-mt-20 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <SectionHeading
            eyebrow="Transparency"
            title="Where your support goes"
            description="Every contribution is intended to help move MastaKash through the immediate survival and readiness phase."
          />

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useOfFunds.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="absolute right-0 top-0 rounded-bl-xl bg-purple-50 px-3 py-1.5 text-xs font-black text-[#5b21b6]">
                    {item.percentage}
                  </div>

                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-100 text-[#5b21b6]">
                    <Icon size={22} />
                  </div>

                  <div className="mt-5 flex items-end justify-between gap-3">
                    <h3 className="font-extrabold text-slate-900">
                      {item.title}
                    </h3>

                    <span className="shrink-0 text-lg font-black text-orange-500">
                      {item.amount}
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50 p-6">
            <div className="flex gap-4">
              <ShieldCheck className="mt-0.5 shrink-0 text-orange-600" />

              <div>
                <h3 className="font-extrabold text-[#29104d]">
                  Important distinction
                </h3>

                <p className="mt-1 text-sm leading-6 text-slate-600">
                  The $10,000 campaign is a survival and regulatory-readiness
                  bridge. It is{" "}
                  <strong>not being represented as the full capitalization</strong>{" "}
                  required for MastaKash's future operations or authorization.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          BRIDGE
      ============================================================ */}
      <section className="overflow-hidden bg-[#29104d] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <SectionHeading
            eyebrow="The Road Ahead"
            title="This $10,000 is a bridge — not the destination."
            description="The immediate campaign is designed to help MastaKash move from survival toward the larger opportunity ahead."
            centered
          />

          <div className="mt-16 grid gap-5 md:grid-cols-4">
            {bridgeSteps.map((step, index) => (
              <div key={step.number} className="relative">
                {index < bridgeSteps.length - 1 && (
                  <div className="absolute left-[calc(100%_-_12px)] top-8 hidden h-px w-full bg-white/10 md:block" />
                )}

                <div className="relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                  <div className="text-sm font-black tracking-[0.2em] text-orange-400">
                    {step.number}
                  </div>

                  <h3 className="mt-5 text-2xl font-black text-white">
                    {step.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-purple-100">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-14 max-w-3xl text-center">
            <p className="text-2xl font-bold leading-10 text-white sm:text-3xl">
              $10,000
              <span className="mx-3 text-orange-400">→</span>
              Readiness
              <span className="mx-3 text-orange-400">→</span>
              Strategic Capital
              <span className="mx-3 text-orange-400">→</span>
              Growth
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          CONTRIBUTION LEVELS
      ============================================================ */}
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <SectionHeading
            eyebrow="Every Contribution Matters"
            title="Choose your level of support"
            description="There is no contribution too small to matter. If you can give more, your support can accelerate the bridge."
            centered
          />

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {contributionLevels.map((level) => (
              <a
                key={level.amount}
                href="#support"
                className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-orange-300 hover:shadow-xl"
              >
                <div className="text-3xl font-black text-[#29104d]">
                  ${level.amount.toLocaleString()}
                </div>

                <div className="mt-3 font-extrabold text-slate-900">
                  MastaKash {level.title}
                </div>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {level.description}
                </p>

                <div className="mt-5 flex items-center gap-1 text-sm font-bold text-orange-500">
                  Support at this level
                  <ArrowRight
                    size={15}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </a>
            ))}
          </div>

          <div className="mt-5 rounded-2xl border-2 border-dashed border-orange-200 bg-orange-50 p-6 text-center">
            <p className="font-bold text-[#29104d]">
              Can you help with $10,000?
            </p>

            <p className="mt-1 text-sm text-slate-600">
              We are also looking for an anchor or bridge partner who can
              finance the immediate campaign in full.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          FOUNDER MESSAGE
      ============================================================ */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-10">
          <div className="overflow-hidden rounded-[2rem] bg-[#29104d] shadow-2xl">
            <div className="grid lg:grid-cols-[.8fr_1.2fr]">
              <div className="relative flex min-h-[300px] items-center justify-center overflow-hidden bg-purple-900/40 p-10">
                <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-orange-500/20 blur-3xl" />

                <div className="relative text-center">
                  <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full border-4 border-orange-400/30 bg-white/10 text-5xl font-black text-orange-400">
                    R
                  </div>

                  <p className="mt-5 text-xl font-black text-white">
                    Ronald Wafula
                  </p>

                  <p className="mt-1 text-sm text-purple-200">
                    Founder & CEO, RoshTech
                  </p>
                </div>
              </div>

              <div className="p-8 sm:p-12">
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-400">
                  A message from the founder
                </p>

                <blockquote className="mt-6 text-2xl font-bold leading-10 text-white sm:text-3xl">
                  "We have invested years of work, our resources and our
                  conviction into MastaKash. We are asking for help not to
                  start again, but to keep moving forward."
                </blockquote>

                <p className="mt-7 leading-8 text-purple-100">
                  The $10,000 fund is about creating enough runway to maintain
                  the technology, continue our readiness work and pursue the
                  strategic capital required to take MastaKash into its next
                  chapter.
                </p>

                <a
                  href="#support"
                  className="mt-8 inline-flex items-center gap-2 font-bold text-orange-400 transition hover:text-orange-300"
                >
                  Stand with MastaKash
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          THREE WAYS TO HELP
      ============================================================ */}
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <SectionHeading
            eyebrow="You Can Help In Three Ways"
            title="Not everyone has to contribute money."
            description="Your network, introduction or voice can be just as valuable as a financial contribution."
            centered
          />

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: CircleDollarSign,
                number: "01",
                title: "Give",
                text: "Make a financial contribution at any level that works for you.",
              },
              {
                icon: HandHeart,
                number: "02",
                title: "Introduce",
                text: "Connect us with investors, strategic partners, institutions or people who understand African fintech.",
              },
              {
                icon: Users,
                number: "03",
                title: "Share",
                text: "Share the MastaKash story with your network and help us reach people we could never reach alone.",
              },
            ].map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.number}
                  className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                      <Icon size={23} />
                    </div>

                    <span className="text-sm font-black text-slate-300">
                      {item.number}
                    </span>
                  </div>

                  <h3 className="mt-7 text-2xl font-black text-[#29104d]">
                    {item.title}
                  </h3>

                  <p className="mt-3 leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          TRANSPARENCY
      ============================================================ */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-10">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl sm:p-12">
            <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-purple-100 text-[#5b21b6]">
                <LockKeyhole size={26} />
              </div>

              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-500">
                  Our Commitment
                </p>

                <h2 className="mt-2 text-3xl font-black text-[#29104d]">
                  Responsible stewardship matters.
                </h2>

                <p className="mt-5 leading-8 text-slate-600">
                  We understand that asking people to support MastaKash creates
                  a responsibility to use those funds carefully. Contributions
                  will be tracked and prioritized against the objectives
                  described in this campaign.
                </p>

                <div className="mt-7 grid gap-4 sm:grid-cols-2">
                  {[
                    "Track funds received",
                    "Record significant expenditure",
                    "Prioritize stated campaign objectives",
                    "Provide appropriate progress updates",
                    "Avoid promising guaranteed regulatory outcomes",
                    "Communicate material campaign changes",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-2 text-sm font-medium text-slate-700"
                    >
                      <CheckCircle2
                        size={18}
                        className="mt-0.5 shrink-0 text-orange-500"
                      />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ
      ============================================================ */}
      <section id="faq" className="scroll-mt-20 bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-10">
          <SectionHeading
            eyebrow="Frequently Asked Questions"
            title="Questions people may have"
            centered
          />

          <div className="mt-12 space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={faq.question}
                className="group rounded-xl border border-slate-200 bg-white"
                open={index === 0}
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 px-6 py-5 font-bold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{faq.question}</span>

                  <ChevronDown
                    size={20}
                    className="shrink-0 text-orange-500 transition-transform group-open:rotate-180"
                  />
                </summary>

                <div className="border-t border-slate-100 px-6 pb-6 pt-4 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
      ============================================================ */}
      <section
        id="support"
        className="scroll-mt-20 relative overflow-hidden bg-[#29104d] py-20 sm:py-28"
      >
        <div className="absolute -left-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-purple-700/30 blur-3xl" />
        <div className="absolute -right-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-orange-500/15 blur-3xl" />

        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-xl shadow-orange-900/30">
            <HandHeart size={30} />
          </div>

          <p className="mt-7 text-sm font-bold uppercase tracking-[0.25em] text-orange-400">
            Help Us Cross The Bridge
          </p>

          <h2 className="mt-4 text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
            The target is $10,000.
            <span className="block text-orange-400">
              The vision is much bigger.
            </span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-purple-100">
            Help us keep MastaKash alive, continue the journey and create the
            runway needed for the next stage.
          </p>

          {/* Replace this link with your actual contribution/payment URL */}
          <a
            href="YOUR_CONTRIBUTION_URL"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-9 inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-8 py-4 text-base font-black text-white shadow-2xl shadow-orange-900/30 transition hover:-translate-y-1 hover:bg-orange-400"
          >
            Support MastaKash
            <ArrowRight size={20} />
          </a>

          <p className="mt-5 text-xs text-purple-300">
            Every contribution matters. Every introduction matters. Every
            share matters.
          </p>
        </div>
      </section>

      {/* ============================================================
          FOOTER
      ============================================================ */}
      <footer className="bg-[#17082c] text-purple-200">
        <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:px-10">
          <div className="grid gap-10 md:grid-cols-[1.3fr_.7fr_.7fr]">
            <div>
              <MastaKashLogo />

              <p className="mt-5 max-w-md text-sm leading-7 text-purple-300">
                MastaKash is a Kenyan-built financial technology platform
                created to help communities manage their financial activities
                digitally.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-white">Campaign</h3>

              <div className="mt-4 space-y-3 text-sm">
                <a
                  href="#story"
                  className="block transition hover:text-orange-400"
                >
                  Our Story
                </a>

                <a
                  href="#use-of-funds"
                  className="block transition hover:text-orange-400"
                >
                  Use of Funds
                </a>

                <a
                  href="#faq"
                  className="block transition hover:text-orange-400"
                >
                  FAQ
                </a>

                <a
                  href="#support"
                  className="block transition hover:text-orange-400"
                >
                  Support MastaKash
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-white">RoshTech</h3>

              <div className="mt-4 space-y-3 text-sm">
                <a
                  href="https://www.roshtech.co.ke"
                  className="block transition hover:text-orange-400" target="_blank"
                >
                  Company Website
                </a>

                <a
                  href="https://www.roshtech.co.ke/contact-us"
                  className="block transition hover:text-orange-400"
                >
                  Contact Us
                </a>

                <a
                  href="https://mastakash.com"
                  className="block transition hover:text-orange-400" target="_blank"
                >
                  Visit MastaKash Website
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-white/10 pt-7 text-xs text-purple-400">
            <div className="flex flex-col justify-between gap-3 sm:flex-row">
              <p>
                © {new Date().getFullYear()} RoshTech Limited. All rights
                reserved.
              </p>

              <p>
                MastaKash Survival & Regulatory Readiness Fund · $10,000 Target
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
     </div>
  );
}